<template>
	<base-modal
		ref="$modal"
		:title="props.title"
		:loading="props.loading"
		width="90vw"
		max-width="90vw"
		height="max-content"
		@on-close="deactivateEsc"
	>
		<template #header-right>
			<app-tooltip :text="$t('refresh_label')">
				<template #activator>
					<app-icon
						name="refresh"
						width="16px"
						color="#979797"
						hover-color="#499259"
						cursor="pointer"
						@click="emit('refresh')"
					/>
				</template>
			</app-tooltip>
		</template>

		<div
			ref="$content"
			class="parameter-expand"
		>
			<table
				class="dialog-table"
				ref="$table"
			>
				<parameter-inline
					v-if="$table"
					is-root
					is-table
					:editable="props.editable"
					:id="props.id"
					:model-value="props.modelValue"
					:token-styles="props.tokenStyles"
					:show-custom="false"
					:value-set-from-instance="true"
					:context-trigger="$table"
					:create-mode="false"
					:show-field-label="props.showFieldLabel"
					@update:modelValue="(value: ParameterInstanceContainer[]) => emit('update:modelValue', value)"
					@title-action="onTitleAction"
				/>
			</table>
		</div>
	</base-modal>
</template>

<script setup lang="ts">
import { ref } from "vue";
import BaseModal from "@/components/common/modal/BaseModal.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import ParameterInline from "@/components/multiedit/temporary/ParameterInline.vue";
import { ParameterInstanceContainer } from "@/models/interfaces/parameters/parameter-models";
import { useModalEscape } from "@/composables/useModalEscape";

type ParameterExpandModalProps = {
	id: string,
	title: string,
	modelValue: ParameterInstanceContainer[],
	editable?: boolean,
	tokenStyles?: Record<string, string>,
	showFieldLabel?: boolean,
	loading?: boolean,
}

const props = withDefaults(defineProps<ParameterExpandModalProps>(), {
	editable: false,
	showFieldLabel: true,
	loading: false,
})

const emit = defineEmits<{
	(e: 'update:modelValue', value: ParameterInstanceContainer[]): void,
	// Контейнер тут один, но обновляется весь блок, отдельного метода на один
	// контейнер в АПИ нет
	(e: 'refresh'): void,
}>()

const $modal = ref<InstanceType<typeof BaseModal> | null>(null)
const $content = ref<HTMLElement | null>(null)
// Таблица нужна параметру как якорь контекстного меню, поэтому он рендерится вторым проходом
const $table = ref<HTMLElement | null>(null)

const { activate: activateEsc, deactivate: deactivateEsc } = useModalEscape({
	content: $content,
	close: () => $modal.value?.onClose()
})

const onTitleAction = (payload: { action: string }) => {
	if (payload.action === "refresh") emit("refresh")
}

const onOpen = () => {
	$modal.value?.onOpen()
	activateEsc()
}
const onClose = () => $modal.value?.onClose()

defineExpose({ onOpen, onClose })
</script>

<style scoped lang="scss">
.parameter-expand {
	width: 100%;
	max-width: 100%;
	min-width: 0;
	min-height: 0;
	padding: 4px 12px 12px 12px;
	overflow: auto;
	box-sizing: border-box;
}

.parameter-expand .dialog-table {
	width: 100%;
	max-width: 100%;
}

.parameter-expand :deep(td:first-child:not(.inline-index-cell)) {
	width: auto;
}

.parameter-expand :deep(.dialog-table td) {
	border-top-width: 0;
}

.parameter-expand :deep(.inline-descriptor--root > tr + tr > td) {
	border-top-width: 1px;
}

.parameter-expand :deep(.inline-title-action) {
	margin-left: 0;
	min-width: 0;
	max-width: 0;
	overflow: hidden;
	opacity: 0;
	pointer-events: none;
}

.parameter-expand:hover :deep(.inline-title-action) {
	margin-left: 4px;
	max-width: 14px;
	overflow: visible;
	opacity: 1;
	pointer-events: auto;
}
</style>
